import { useMemo } from "react"

import { useCurrentOverlay } from "shared/context/overlay/OverlayIDContext"
import {
  PRODUCT_CONFIGURATION_OVERLAY,
  PRODUCT_CONFIGURATOR_MODE_SELECT_OVERLAY
} from "shared/context/overlay/types"
import { Logger } from "shared/lib/Logger"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import { ProductConfiguratorMode } from "shared/types/ProductConfiguratorMode"

import { useExternalDimensioningModelsQuery } from "entities/dim-configuration/graphql/external-dimensioning.queries.generated"
import { useProductConfigurationModelsQuery } from "entities/dim-configuration/graphql/product-configuration.queries.generated"
import { usePortfolioProductSelectModel } from "widgets/portfolio-product-select/model/usePortfolioProductSelectModel"

import { PortfolioProductSelectOverlayProps } from "../PortfolioProductSelectOverlay"

export function usePortfolioProductSelectOverlayModel({
  payload
}: PortfolioProductSelectOverlayProps) {
  const { onSubmit } = payload
  const { closeOverlay } = useCurrentOverlay()
  const { openOverlay: openProductConfigurationOverlay } = useOverlay(
    PRODUCT_CONFIGURATION_OVERLAY
  )
  const { openOverlay: openProductConfiguratorModeSelectOverlay } = useOverlay(
    PRODUCT_CONFIGURATOR_MODE_SELECT_OVERLAY
  )

  const { data: configurationModelsData } = useProductConfigurationModelsQuery()
  const configurationModels = useMemo(
    () =>
      configurationModelsData?.configurationModels?.models?.map(
        configurationModel => configurationModel.id
      ),
    [configurationModelsData]
  )
  const { data: dimensioningModelsData } = useExternalDimensioningModelsQuery()
  const dimensioningModels = useMemo(
    () =>
      dimensioningModelsData?.externalDimensioningModels?.models?.map(
        dimensioningModel => dimensioningModel?.id
      ),
    [dimensioningModelsData]
  )

  const handleOpenConfigurator = useEvent(
    (productType: string, mode: ProductConfiguratorMode) => {
      openProductConfigurationOverlay({
        configuration: {
          variantKey: productType
        },
        mode,
        onSubmit
      })
    }
  )

  const openConfigurator = useEvent((productType: string) => {
    if (
      configurationModels?.includes(productType) &&
      dimensioningModels?.includes(productType)
    ) {
      openProductConfiguratorModeSelectOverlay({
        productType,
        onProductConfigurationChange: onSubmit
      })
    } else if (configurationModels?.includes(productType)) {
      handleOpenConfigurator(productType, ProductConfiguratorMode.CONFIGURATION)
    } else if (dimensioningModels?.includes(productType)) {
      handleOpenConfigurator(productType, ProductConfiguratorMode.DIMENSIONING)
    } else {
      Logger.error(
        `no configuration or dimensioning model found for product type ${productType}`
      )
    }
    closeOverlay()
  })

  const model = usePortfolioProductSelectModel({
    onProductSelect: openConfigurator
  })

  return model
}
